import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, MessageCircle, GraduationCap } from 'lucide-react';
export function AcademySection() {
  const fadeUp = {
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        delay: i * 0.15,
        ease: 'easeOut'
      }
    })
  };
  const benefits = [{
    title: 'No necesitás experiencia',
    desc: 'Empezá desde cero, nosotros te guiamos.'
  }, {
    title: 'Práctica real desde el primer día',
    desc: 'Aprendé haciendo, con modelos reales.'
  }, {
    title: 'Formación pensada para generar ingresos',
    desc: 'Un oficio con demanda y oportunidades reales.'
  }];
  return <section id="academia" className="relative py-24 md:py-32 overflow-hidden" data-id="element-54">
      {/* Subtle background texture */}
      <div className="absolute inset-0 bg-dark" data-id="element-55" />
      <div className="absolute inset-0 opacity-[0.03] bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMtOS45NDEgMC0xOCA4LjA1OS0xOCAxOHM4LjA1OSAxOCAxOCAxOCAxOC04LjA1OSAxOC0xOC04LjA1OS0xOC0xOC0xOHptMCAzMmMtNy43MzIgMC0xNC02LjI2OC0xNC0xNHM2LjI2OC0xNCAxNC0xNCAxNCA2LjI2OCAxNCAxNC02LjI2OCAxNC0xNCAxNHoiIHN0cm9rZT0iI2ZmZiIgc3Ryb2tlLXdpZHRoPSIuNSIvPjwvZz48L3N2Zz4=')]" data-id="element-56" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-57">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center" data-id="element-58">
          {/* LEFT: Text content */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} className="order-2 lg:order-1" data-id="element-59">
            <motion.div custom={0} variants={fadeUp} className="inline-flex items-center gap-2 text-gold text-xs font-bold tracking-[0.25em] uppercase mb-4" data-id="element-60">
              <GraduationCap size={14} data-id="element-61" />
              <span data-id="element-62">Cursos Iniciales 2026</span>
            </motion.div>

            <motion.h2 custom={1} variants={fadeUp} className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6 leading-[1.05] uppercase tracking-tight" data-id="element-63">
              Formamos
              <br data-id="element-64" />
              Barberos/as.
            </motion.h2>

            <motion.p custom={2} variants={fadeUp} className="text-xl md:text-2xl text-gold font-semibold mb-6 leading-snug" data-id="element-65">
              Aprendé un oficio con salida laboral real y empezá a construir tu
              propio ingreso.
            </motion.p>

            <motion.p custom={3} variants={fadeUp} className="text-gray-400 leading-relaxed mb-10 text-base md:text-lg" data-id="element-66">
              No necesitás experiencia previa. Te enseñamos desde cero, con
              práctica real y acompañamiento para que puedas empezar a crecer en
              la barbería de verdad.
            </motion.p>

            {/* Benefits */}
            <motion.ul custom={4} variants={fadeUp} className="space-y-5 mb-10 divide-y divide-white/5" data-id="element-67">
              {benefits.map((benefit, index) => <li key={index} className={`flex items-start gap-4 ${index > 0 ? 'pt-5' : ''}`} data-id="element-68">
                  <span className="flex-shrink-0 mt-0.5 w-8 h-8 rounded-full border-2 border-gold/60 flex items-center justify-center" data-id="element-69">
                    <CheckCircle2 className="text-gold" size={18} strokeWidth={2.5} data-id="element-70" />
                  </span>
                  <div data-id="element-71">
                    <p className="text-white font-bold text-base md:text-lg leading-tight mb-1" data-id="element-72">
                      {benefit.title}
                    </p>
                    <p className="text-gray-400 text-sm md:text-base" data-id="element-73">
                      {benefit.desc}
                    </p>
                  </div>
                </li>)}
            </motion.ul>

            <motion.div custom={5} variants={fadeUp} data-id="element-74">
              <a href="https://wa.me/59893472053" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#20BD5A] text-white font-bold py-4 px-8 rounded-full transition-all duration-300 transform hover:-translate-y-1 shadow-[0_0_20px_rgba(37,211,102,0.2)] w-full sm:w-auto" data-id="element-75">
                <MessageCircle size={22} data-id="element-76" />
                <span data-id="element-77">Consultar cursos por WhatsApp</span>
              </a>
            </motion.div>
          </motion.div>

          {/* RIGHT: Editorial overlapping photos */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} className="relative h-[520px] sm:h-[600px] lg:h-[680px] order-1 lg:order-2" data-id="element-78">
            <motion.div custom={0} variants={fadeUp} className="absolute top-0 right-0 w-[78%] sm:w-[75%] h-[60%] rounded-[20px] overflow-hidden shadow-2xl shadow-black/60 border border-white/5" data-id="element-79">
              <img src="https://cdn.magicpatterns.com/uploads/deVhXUqu7UqT8YY7v36FNa/WhatsApp_Image_2026-05-02_at_7.55.03_AM_(3).jpg" alt="Alumnos graduados del curso de barbería" className="w-full h-full object-cover object-center" data-id="element-80" />
            </motion.div>

            <motion.div custom={1} variants={fadeUp} className="absolute bottom-0 left-0 w-[55%] sm:w-[52%] h-[68%] rounded-[20px] overflow-hidden shadow-2xl shadow-black/60 border border-gold/20 z-10" data-id="element-81">
              <img src="https://cdn.magicpatterns.com/uploads/4caj3HyQocyTzAZiTRYS3e/WhatsApp_Image_2026-05-02_at_8.02.38_AM_(3).jpg" alt="Práctica de barbería con máquina dorada" className="w-full h-full object-cover object-[center_30%]" data-id="element-82" />
            </motion.div>

            <div className="absolute -bottom-4 -right-4 w-24 h-24 border-r-2 border-b-2 border-gold/30 rounded-br-3xl z-0" data-id="element-83" />
            <div className="absolute -top-4 -left-4 w-24 h-24 border-l-2 border-t-2 border-gold/30 rounded-tl-3xl z-0" data-id="element-84" />
          </motion.div>
        </div>
      </div>
    </section>;
}